import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import styled from 'styled-components';
import { operationType, selectDataType } from "../../../../services/types";
import { updateNodeValue } from "../../../../services/method";
import { Textarea } from "../../../form/textarea";
import { Select } from "../../../form/select";
import { TextInput } from "../../../form/textInput";
import { SettingsBlock, settingsDataType } from "../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../expanded";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { useSearchParams } from "react-router-dom";
import { webURL } from "../../../../services/API";
import axios from "axios";
import Cookies from "universal-cookie";

type AiAgentProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function AiAgent({ id, data, setChildMinimize, setChildSettings }: AiAgentProps) {
   const [models, setModels] = React.useState<selectDataType[]>([
      { id: 1, name: 'Fast (Gemini 2.5 Flash)', refId: 'gemini-2.5-flash', isActive: true },
      { id: 2, name: 'Extra Fast (Gemini 2.5 Flash Lite)', refId: 'gemini-2.5-flash-lite', isActive: false },
      { id: 3, name: 'Stable (Gemini 2.0 Flash)', refId: 'gemini-2.0-flash', isActive: false },
      { id: 4, name: 'Budget (Gemini 2.0 Flash Lite)', refId: 'gemini-2.0-flash-lite', isActive: false },
      { id: 5, name: 'OpenAI (GPT-4o mini)', refId: 'gpt-4o-mini', isActive: false },
      { id: 6, name: 'GPT-5.4', refId: 'gpt-5.4', isActive: false },
      { id: 7, name: 'GPT-5.4 Mini', refId: 'gpt-5.4-mini', isActive: false },
      { id: 8, name: 'GPT-5.4 Nano', refId: 'gpt-5.4-nano', isActive: false },
      { id: 9, name: 'Gemini 3.1 Pro Preview', refId: 'gemini-3.1-pro-preview', isActive: false },
      { id: 10, name: 'Gemini 3.1 Flash Lite Preview', refId: 'gemini-3.1-flash-lite-preview', isActive: false },
      { id: 11, name: 'Gemini 3 Flash Preview', refId: 'gemini-3-flash-preview', isActive: false },
   ]);
   const [reasoning, setReasoning] = React.useState<selectDataType[]>([
      { id: 1, name: 'Auto (adaptive)', refId: 'auto', isActive: true },
      { id: 2, name: 'Low (5 iterations)', refId: '5', isActive: false },
      { id: 3, name: 'Medium (15 iterations)', refId: '15', isActive: false },
      { id: 4, name: 'High (30 iterations)', refId: '30', isActive: false },
      { id: 5, name: 'Very High (45 iterations)', refId: '45', isActive: false },
   ]);
   const [memory, setMemory] = React.useState<selectDataType[]>([
      { id: 1, name: 'Remember conversation', refId: 'simple-memory', isActive: true },
      { id: 2, name: 'Do not remember', refId: 'no-memory', isActive: false },
   ]);
   const [promptSource, setPromptSource] = React.useState<selectDataType[]>([
      { id: 1, name: 'Use previous node input', refId: 'auto', isActive: true },
      { id: 2, name: 'Write custom prompt', refId: 'manual', isActive: false },
   ]);
   const [showAdvanced, setShowAdvanced] = React.useState(false);
   const { setNodes, getNodes, getEdges } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [searchParams] = useSearchParams();
   const projectId = searchParams.get('id');
   const userId = useSelector((state: RootState) => state?.user.data?._id);
   const initializedRef = React.useRef(false);
   const establishedThreadRef = React.useRef<string | null>(null);
   const textUpdateTimersRef = React.useRef<{ [key: string]: ReturnType<typeof setTimeout> | null }>({});
   const markdownUploadInputRef = React.useRef<HTMLInputElement | null>(null);

   const getAuthHeaders = React.useCallback(() => {
      const token = new Cookies().get('token');
      return { headers: { 'x-auth-token': token } };
   }, []);

   const getThreadId = React.useCallback((mode: 'user' | 'admin' = 'user') => {
      if (!projectId || !data.index || !userId) return '';
      const prefix = mode === 'admin' ? 'admin' : 'user';
      return `${projectId}-${data.index}-${prefix}-${userId}`;
   }, [projectId, data.index, userId]);

   const establishAgentMemory = async (threadId: string) => {
      const requestBody: any = {
         thread_id: threadId,
         projectId: projectId,
         agentIndex: data.index
      }
      await axios.post(`${webURL}/agents/aiAgentChat`, requestBody).catch((Err) => { console.error(Err) });
   }

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (initializedRef.current) return;
      initializedRef.current = true;
      setModels((models) => models.map((model) => ({ ...model, isActive: model.refId === (data.value?.model || models[0].refId) })));
      setMemory((memory) => memory.map((m) => ({ ...m, isActive: m.refId === (data.value?.memory || memory[0].refId) })));
      const resolvedReasoning = (() => {
         const raw = String(data.value?.reasoning ?? data.value?.maxIterations ?? reasoning[0].refId).trim().toLowerCase();
         if (['auto', '5', '15', '30', '45'].includes(raw)) return raw;
         return reasoning[0].refId as string;
      })();
      setReasoning((reasoning) => reasoning.map((r) => ({ ...r, isActive: r.refId === resolvedReasoning })));
      setPromptSource((promptSource) => promptSource.map((ps) => ({ ...ps, isActive: ps.refId === (data.value?.promptSource || promptSource[0].refId) })));

      if (!data.value?.model) updateNodeValue(id, setNodes, { 
         model: models[0].refId, 
         memory: memory[0].refId, 
         reasoning: resolvedReasoning,
         maxIterations: resolvedReasoning === 'auto' ? 'auto' : parseInt(resolvedReasoning, 10),
         temperature: 0.0,
         promptSource: promptSource[0].refId,
         returnIntermediateSteps: false
      });
      else if (!data.value?.reasoning && data.value?.maxIterations === undefined) {
         updateNodeValue(id, setNodes, {
            reasoning: resolvedReasoning,
            maxIterations: resolvedReasoning === 'auto' ? 'auto' : parseInt(resolvedReasoning, 10),
         });
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   React.useEffect(() => {
      const userThreadId = getThreadId('user');
      const adminThreadId = getThreadId('admin');
      if (!userThreadId) return;
      if (data.value?.memory === 'no-memory') return;
      if (establishedThreadRef.current === userThreadId) return;

      establishedThreadRef.current = userThreadId;
      establishAgentMemory(userThreadId);
      establishAgentMemory(adminThreadId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [getThreadId, data.value?.memory]);

   React.useEffect(() => {
      return () => {
         Object.values(textUpdateTimersRef.current).forEach((timer) => {
            if (timer) clearTimeout(timer);
         });
      };
   }, []);


   const handleSelectItem = async (type: string, selectId: number) => {
      let refId = '';

      if (type === 'model') {
         setModels((models) => models.map((model) => {
            if (model.id === selectId) return { ...model, isActive: true };
            else return { ...model, isActive: false };
         }));
         refId = models.find(obj => obj.id === selectId)?.refId as string;
      } else if (type === 'memory') {
         setMemory((memory) => memory.map((m) => {
            if (m.id === selectId) return { ...m, isActive: true };
            else return { ...m, isActive: false };
         }));
         refId = memory.find(obj => obj.id === selectId)?.refId as string;
      } else if (type === 'reasoning') {
         setReasoning((reasoning) => reasoning.map((r) => {
            if (r.id === selectId) return { ...r, isActive: true };
            else return { ...r, isActive: false };
         }));
         refId = reasoning.find(obj => obj.id === selectId)?.refId as string;
         updateNodeValue(id, setNodes, {
            reasoning: refId,
            maxIterations: refId === 'auto' ? 'auto' : parseInt(refId, 10),
         });
         return;
      } else if (type === 'promptSource') {
         setPromptSource((promptSource) => promptSource.map((ps) => {
            if (ps.id === selectId) return { ...ps, isActive: true };
            else return { ...ps, isActive: false };
         }));
         refId = promptSource.find(obj => obj.id === selectId)?.refId as string;
      }
      updateNodeValue(id, setNodes, { [type]: refId });
   }

   // ---
   const handleChangeText = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (!keyRef) return;
      const nextValue = e.target.value;
      const existingTimer = textUpdateTimersRef.current[keyRef];
      if (existingTimer) clearTimeout(existingTimer);

      // Debounce updates to avoid cursor jumps in long text fields.
      textUpdateTimersRef.current[keyRef] = setTimeout(() => {
         updateNodeValue(id, setNodes, { [keyRef]: nextValue });
      }, 180);
   }

   const handleChangeTemperature = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      const rawValue = e.target.value.replace(/,/g, '.');
      if (rawValue === '') {
         updateNodeValue(id, setNodes, { [keyRef as string]: '' });
         return;
      }

      // Keep partial typing experience smooth (e.g. "0.")
      if (rawValue.endsWith('.')) {
         updateNodeValue(id, setNodes, { [keyRef as string]: rawValue });
         return;
      }

      const temperature = Number(rawValue);
      // Limit to safe range similar to conservative LLM usage
      if (!Number.isNaN(temperature) && temperature <= 1 && temperature >= 0)
         updateNodeValue(id, setNodes, { [keyRef as string]: rawValue });
   }

   const handleChangeSessionId = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      updateNodeValue(id, setNodes, { [keyRef as string]: e.target.value });
   }

   const handleMarkdownRawChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, keyRef?: string) => {
      const nextRaw = e.target.value;
      if (!nextRaw.trim()) {
         updateNodeValue(id, setNodes, {
            [keyRef as string]: '',
            markdownConfig: null,
            markdownConfigStatus: 'empty',
            markdownConfigErrors: [],
            markdownConfigWarnings: [],
            markdownFileName: '',
         });
         return;
      }

      updateNodeValue(id, setNodes, { [keyRef as string]: nextRaw });
   }

   const validateMarkdownForNode = React.useCallback(async (markdownRaw: string, fileName?: string) => {
      const markdown = String(markdownRaw || '');
      if (!markdown.trim()) {
         updateNodeValue(id, setNodes, {
            markdownConfigRaw: '',
            markdownConfig: null,
            markdownConfigStatus: 'empty',
            markdownConfigErrors: [],
            markdownConfigWarnings: [],
            markdownFileName: '',
         });
         return;
      }

      updateNodeValue(id, setNodes, {
         markdownConfigRaw: markdown,
         markdownConfigStatus: 'parsing',
         markdownConfigErrors: [],
         markdownConfigWarnings: [],
         markdownFileName: fileName || data.value?.markdownFileName || '',
      });

      try {
         const { data: parsed } = await axios.post(
            `${webURL}/agents/studio/parse-markdown-config`,
            {
               markdown,
               fileName: fileName || data.value?.markdownFileName || '',
               systemPrompt: data.value?.systemMessage || '',
               userPrompt: data.value?.manualPrompt || '',
            },
            getAuthHeaders()
         );

         updateNodeValue(id, setNodes, {
            markdownConfigRaw: markdown,
            markdownConfig: parsed?.config || null,
            markdownConfigStatus: parsed?.isValid ? 'valid' : 'invalid',
            markdownConfigErrors: parsed?.errors || [],
            markdownConfigWarnings: parsed?.warnings || [],
            markdownFileName: parsed?.markdownFileName || fileName || data.value?.markdownFileName || '',
         });
      } catch (err: any) {
         const message = err?.response?.data?.error || err?.message || 'Failed to validate markdown config';
         updateNodeValue(id, setNodes, {
            markdownConfigRaw: markdown,
            markdownConfig: null,
            markdownConfigStatus: 'invalid',
            markdownConfigErrors: [message],
            markdownConfigWarnings: [],
            markdownFileName: fileName || data.value?.markdownFileName || '',
         });
      }
   }, [data.value?.markdownFileName, getAuthHeaders, id, setNodes]);

   const handleMarkdownFilePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.name.toLowerCase().endsWith('.md')) {
         updateNodeValue(id, setNodes, {
            markdownConfigStatus: 'invalid',
            markdownConfigErrors: ['Only Markdown (.md) files are supported.'],
            markdownConfigWarnings: [],
         });
         event.target.value = '';
         return;
      }

      try {
         const markdown = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result || ''));
            reader.onerror = () => reject(new Error('Failed to read markdown file'));
            reader.readAsText(file);
         });
         await validateMarkdownForNode(markdown, file.name);
      } catch (err: any) {
         const message = err?.message || 'Failed to parse markdown file';
         updateNodeValue(id, setNodes, {
            markdownConfigStatus: 'invalid',
            markdownConfigErrors: [message],
            markdownConfigWarnings: [],
         });
      } finally {
         event.target.value = '';
      }
   }

   const removeMarkdownForNode = () => {
      updateNodeValue(id, setNodes, {
         markdownConfigRaw: '',
         markdownConfig: null,
         markdownConfigStatus: 'empty',
         markdownConfigErrors: [],
         markdownConfigWarnings: [],
         markdownFileName: '',
      });
   }

   // ---
   const isWarning = false;
   const nodes = getNodes();
   const edges = getEdges();
   const agentNodes = nodes.map(node => { if (node.data.action === "aiAgent") return node.id });
   let orchestrators: any = [];
   let subagents: any = [];
   edges.map(edge => {
      if (agentNodes.includes(edge.source) && agentNodes.includes(edge.target)) {
         orchestrators.push(edge.source);
         subagents.push(edge.target);
      }
   })




   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {subagents?.includes(id) && <span id="subAgent" >Sub-agent</span>}
      
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}


         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ width: '480px' }}
         ><ConfigContainer>
               {/* Quick Setup */}
               <BasicSettingsSection>
                  <SettingBlock>
                     <SettingLabel>Model</SettingLabel>
                     <Select title="" data={models} handleSelectItem={(selectId: number) => handleSelectItem('model', selectId)} isSmall={true} />
                     <HelpText>Choose speed and quality profile.</HelpText>
                  </SettingBlock>

                  <SettingBlock>
                     <SettingLabel>Memory</SettingLabel>
                     <Select title="" data={memory} handleSelectItem={(selectId: number) => handleSelectItem('memory', selectId)} isSmall={true} />
                     <HelpText>Keep context across user turns or start fresh every run.</HelpText>
                  </SettingBlock>

                  <SettingBlock>
                     <SettingLabel>Input Source</SettingLabel>
                     <Select title="" data={promptSource} handleSelectItem={(selectId: number) => handleSelectItem('promptSource', selectId)} isSmall={true} />
                     <HelpText>Use incoming workflow input or custom text.</HelpText>
                  </SettingBlock>

                  {data.value?.promptSource === 'manual' && (
                     <SettingBlock>
                        <SettingLabel>Custom Prompt</SettingLabel>
                        <TextareaWrapper>
                           <Textarea keyRef="manualPrompt" value={data.value?.manualPrompt} placeholder="Write what the agent should do..." handleChange={handleChangeText} />
                        </TextareaWrapper>
                        <HelpText>This text becomes the main agent input.</HelpText>
                     </SettingBlock>
                  )}

                  <SettingBlock>
                     <SettingLabel>System Message (Optional)</SettingLabel>
                     <TextareaWrapper>
                        <Textarea keyRef="systemMessage" value={data.value?.systemMessage} placeholder="Define role, tone, or constraints..." handleChange={handleChangeText} />
                     </TextareaWrapper>
                     <HelpText>Use only when you need strict behavior.</HelpText>
                  </SettingBlock>
               </BasicSettingsSection>

               <AdvancedToggle onClick={() => setShowAdvanced(!showAdvanced)}>
                  <ToggleIcon isOpen={showAdvanced}>⊳</ToggleIcon>
                  <ToggleText>More Options</ToggleText>
                  <ToggleCount>{showAdvanced ? '(shown)' : '(hidden)'}</ToggleCount>
               </AdvancedToggle>

               {showAdvanced && (
                  <AdvancedSettingsSection>
                     <SettingBlock>
                        <SettingLabel>Max Iterations</SettingLabel>
                        <Select title="" data={reasoning} handleSelectItem={(selectId: number) => handleSelectItem('reasoning', selectId)} isSmall={true} />
                        <HelpText>Upper limit for tool-call loops before the agent stops.</HelpText>
                     </SettingBlock>

                     <SettingBlock>
                        <SettingLabel>Temperature</SettingLabel>
                        <TextInput
                           type="number"
                           keyRef="temperature"
                           value={data.value?.temperature?.toString()}
                           title=""
                           isNormal={false}
                           placeholder="0.0 - 1.0"
                           handleChange={handleChangeTemperature}
                           max={1} min={0.0} step={0.1}
                        />
                        <HelpText>0 = predictable, 1 = creative.</HelpText>
                     </SettingBlock>

                     <SettingBlock>
                        <SettingLabel>Output Format</SettingLabel>
                        <TextareaWrapper>
                           <Textarea keyRef="outputFormat" value={data.value?.outputFormat} placeholder="Optional: JSON, Markdown, plain text..." handleChange={handleChangeText} />
                        </TextareaWrapper>
                        <HelpText>Optional formatting instruction for final response.</HelpText>
                     </SettingBlock>

                     <SettingBlock>
                        <SettingLabel>Markdown Tools/Skills (.md)</SettingLabel>
                        <input
                           ref={markdownUploadInputRef}
                           type="file"
                           accept=".md,text/markdown"
                           onChange={handleMarkdownFilePick}
                           style={{ display: 'none' }}
                        />

                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                           <button
                              type="button"
                              onClick={() => markdownUploadInputRef.current?.click()}
                              style={{
                                 border: '1px solid #cbd5e1',
                                 background: '#f8fafc',
                                 color: '#334155',
                                 borderRadius: 6,
                                 fontSize: 11,
                                 fontWeight: 700,
                                 padding: '5px 8px',
                                 cursor: 'pointer',
                              }}
                           >
                              Upload
                           </button>
                           <button
                              type="button"
                              onClick={() => validateMarkdownForNode(data.value?.markdownConfigRaw || '', data.value?.markdownFileName || '')}
                              disabled={!String(data.value?.markdownConfigRaw || '').trim() || data.value?.markdownConfigStatus === 'parsing'}
                              style={{
                                 border: '1px solid #bfdbfe',
                                 background: '#eff6ff',
                                 color: '#1d4ed8',
                                 borderRadius: 6,
                                 fontSize: 11,
                                 fontWeight: 700,
                                 padding: '5px 8px',
                                 cursor: 'pointer',
                                 opacity: !String(data.value?.markdownConfigRaw || '').trim() ? 0.5 : 1,
                              }}
                           >
                              {data.value?.markdownConfigStatus === 'parsing' ? 'Validating...' : 'Validate'}
                           </button>
                           <button
                              type="button"
                              onClick={removeMarkdownForNode}
                              disabled={!String(data.value?.markdownConfigRaw || '').trim()}
                              style={{
                                 border: '1px solid #fecaca',
                                 background: '#fef2f2',
                                 color: '#b91c1c',
                                 borderRadius: 6,
                                 fontSize: 11,
                                 fontWeight: 700,
                                 padding: '5px 8px',
                                 cursor: 'pointer',
                                 opacity: !String(data.value?.markdownConfigRaw || '').trim() ? 0.5 : 1,
                              }}
                           >
                              Remove
                           </button>

                           <span style={{
                              fontSize: 10,
                              fontWeight: 800,
                              letterSpacing: 0.3,
                              textTransform: 'uppercase',
                              color:
                                 data.value?.markdownConfigStatus === 'valid' ? '#166534' :
                                    data.value?.markdownConfigStatus === 'invalid' ? '#b91c1c' :
                                       data.value?.markdownConfigStatus === 'parsing' ? '#92400e' : '#475569',
                           }}>
                              {data.value?.markdownConfigStatus || 'empty'}
                           </span>
                        </div>

                        {data.value?.markdownFileName ? (
                           <HelpText>Attached: {data.value?.markdownFileName}</HelpText>
                        ) : (
                           <HelpText>Upload markdown with sections: Instructions, Tools, Rules, Examples.</HelpText>
                        )}

                        <TextareaWrapper>
                           <Textarea
                              keyRef="markdownConfigRaw"
                              value={data.value?.markdownConfigRaw}
                              placeholder={'# Agent Instructions\n- ...\n\n# Tools\n- name: ...\n  description: ...\n\n# Rules\n- ...\n\n# Examples\n...'}
                              handleChange={handleMarkdownRawChange}
                           />
                        </TextareaWrapper>
                        <HelpText>Manual edits are supported. Click Validate to refresh parsed structure.</HelpText>

                        {!!data.value?.markdownConfigErrors?.length && (
                           <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
                              {data.value?.markdownConfigErrors?.slice(0, 3).map((item: string, index: number) => (
                                 <div key={`md-err-${index}`} style={{
                                    border: '1px solid #fecaca',
                                    background: '#fef2f2',
                                    color: '#b91c1c',
                                    borderRadius: 6,
                                    padding: '6px 8px',
                                    fontSize: 11,
                                    lineHeight: 1.4,
                                 }}>
                                    {item}
                                 </div>
                              ))}
                           </div>
                        )}

                        {!!data.value?.markdownConfigWarnings?.length && (
                           <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
                              {data.value?.markdownConfigWarnings?.slice(0, 2).map((item: string, index: number) => (
                                 <div key={`md-warn-${index}`} style={{
                                    border: '1px solid #fde68a',
                                    background: '#fffbeb',
                                    color: '#92400e',
                                    borderRadius: 6,
                                    padding: '6px 8px',
                                    fontSize: 11,
                                    lineHeight: 1.4,
                                 }}>
                                    {item}
                                 </div>
                              ))}
                           </div>
                        )}

                        {!!data.value?.markdownConfig && data.value?.markdownConfigStatus === 'valid' && (
                           <div style={{
                              marginTop: 8,
                              border: '1px solid #dbeafe',
                              background: '#eff6ff',
                              borderRadius: 8,
                              padding: '8px 10px',
                              fontSize: 11,
                              color: '#1e3a8a',
                              lineHeight: 1.4,
                              display: 'grid',
                              gap: 3,
                           }}>
                              <div>Instructions: {data.value?.markdownConfig?.instructions?.length || 0}</div>
                              <div>Tools: {data.value?.markdownConfig?.tools?.length || 0}</div>
                              <div>Rules: {data.value?.markdownConfig?.rules?.length || 0}</div>
                              <div>Examples: {data.value?.markdownConfig?.examples?.length || 0}</div>
                           </div>
                        )}
                     </SettingBlock>

                     <SettingBlock>
                        <SettingLabel>Session ID (Optional)</SettingLabel>
                        <TextInput
                           keyRef="SessionId"
                           value={data.value?.SessionId?.toString()}
                           title=""
                           isNormal={false}
                           placeholder="Leave empty to auto-use current user"
                           handleChange={handleChangeSessionId}
                        />
                        <HelpText>Set only if you need custom conversation grouping.</HelpText>
                     </SettingBlock>

                     <CheckboxBlock>
                        <CheckboxInput
                           type="checkbox"
                           id="returnIntermediateSteps"
                           checked={data.value?.returnIntermediateSteps || false}
                           onChange={(e) => updateNodeValue(id, setNodes, { returnIntermediateSteps: e.target.checked })}
                        />
                        <CheckboxLabelNew htmlFor="returnIntermediateSteps">
                           Show Intermediate Steps
                        </CheckboxLabelNew>
                     </CheckboxBlock>
                     <CheckboxHelpText>Debug mode: include internal execution details in output.</CheckboxHelpText>
                  </AdvancedSettingsSection>
               )}

            </ConfigContainer></ExpandedBlock> : null}
      </Main>
   </>);
}

// ---
const Main = styled.div`
   min-width: 150px;
   padding: 0;
`;

// ---
export const ConfigContainer = styled.div`
   display: flex;
   flex-direction: column;
   gap: 0;
   padding-top: 2px;
   padding-right: 15px;
   background: #ffffff;
   border-radius: 8px;
   height: 420px;
   overflow-y: auto;
   overflow-x: hidden;
   
   /* &::-webkit-scrollbar {
      width: 6px;
   }
   
   &::-webkit-scrollbar-track {
      background: #f5f5f5;
      border-radius: 10px;
   }
   
   &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 10px;
      
      &:hover {
         background: #999;
      }
   } */
`;

export const ConfigHeader = styled.div`
   padding: 12px 16px;
   background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
   border-radius: 8px 8px 0 0;
   margin: 0;
   border-bottom: none;
   display: flex;
   align-items: center;
   gap: 8px;
`;

export const ConfigTitle = styled.h2`
   margin: 0;
   font-size: 15px;
   font-weight: 700;
   color: white;
   letter-spacing: 0.3px;
`;

export const BasicSettingsSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 0;
   // padding: 16px;

   .select {
      border-radius: 6px;
   }
`;

export const AdvancedSettingsSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 0;
   padding: 12px 16px 16px 16px;
   background: #f8fafc;
   border-top: 1px solid #e8e8eb;
   animation: slideDown 0.3s ease-out;
   
   @keyframes slideDown {
      from {
         opacity: 0;
         transform: translateY(-10px);
      }
      to {
         opacity: 1;
         transform: translateY(0);
      }
   }
`;

export const SettingBlock = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin-bottom: 14px;
   padding: 10px 12px;
   background: white;
   border: 1px solid #e5e7eb;
   border-radius: 8px;
   transition: all 0.2s ease;
   position: relative;
   // z-index: 1;

   &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 3px;
      height: 100%;
      background: linear-gradient(180deg, #7dd3fc 0%, #38bdf8 100%);
      opacity: 0;
      transition: opacity 0.2s ease;
   }
   
   &:hover {
      border-color: #7dd3fc;
      box-shadow: 0 6px 16px rgba(56, 189, 248, 0.16);
      // transform: translateY(-1px);
      // z-index: 10;

      &::before {
         opacity: 1;
      }
   }

   /* &:focus-within {
      z-index: 12;
   } */
   
   &:last-child {
      margin-bottom: 0;
   }

   .select {
      margin: 0;
      
      h4 {
         display: none;
      }
   }

   .textInput {
      margin: 0;
      
      h4 {
         display: none;
      }
   }
`;

export const AdvancedToggle = styled.button`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 10px 16px;
   background: #f8fafc;
   border: none;
   border-top: 1px solid #e0e0e0;
   border-bottom: 1px solid #e0e0e0;
   cursor: pointer;
   transition: all 0.2s ease;
   font-weight: 600;
   color: #334155;
   font-size: 12px;
   letter-spacing: 0.3px;
   
   &:hover {
      background: #f1f5f9;
   }
   
   &:active {
      background: #dce3eb;
   }
`;

export const ToggleIcon = styled.span<{ isOpen: boolean }>`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-size: 14px;
   transition: transform 0.3s ease;
   transform: rotate(${props => props.isOpen ? '90deg' : '0deg'});
   color: #64748b;
`;

export const ToggleText = styled.span`
   flex: 1;
   text-align: left;
`;

export const ToggleCount = styled.span`
   font-size: 11px;
   color: #94a3b8;
   font-weight: 400;
   text-transform: lowercase;
`;

export const SettingLabel = styled.label`
   font-size: 12px;
   font-weight: 600;
   color: #334155;
   display: block;
   margin-bottom: 4px;
   text-transform: uppercase;
   letter-spacing: 0.4px;
`;

export const HelpText = styled.div`
   font-size: 11px;
   color: #64748b;
   font-style: normal;
   line-height: 1.3;
   margin-top: 4px;
   padding: 0;
`;

export const TextareaWrapper = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;

   .textarea {
      margin: 0;
      
      .title {
         display: none;
      }
   }
   
   /* Override the editor wrapper inside SettingBlock */
   .textarea > div {
      width: 100% !important;
      min-width: 100% !important;
      min-height: 80px;
      max-height: 120px;
      height: 80px;
      border-radius: 6px;
      border: 1px solid #ddd;
      background-color: #fefefe;
      
      &:focus-within {
         border-color: #667eea;
         box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
      }
   }

   textarea {
      font-size: 12px !important;
   }

   pre {
      font-size: 12px !important;
   }
`;

export const TextareaLabel = styled.label`
   font-size: 13px;
   font-weight: 600;
   color: #333;
   display: block;
   text-transform: capitalize;
`;

export const CheckboxBlock = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   margin-bottom: 6px;
   padding: 10px 12px;
   background: white;
   border: 1px solid #e5e7eb;
   border-radius: 8px;
   transition: all 0.2s ease;
   position: relative;
      z-index: 1;

   &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 3px;
      height: 100%;
      background: linear-gradient(180deg, #7dd3fc 0%, #38bdf8 100%);
      opacity: 0;
      transition: opacity 0.2s ease;
   }
   
   &:hover {
      border-color: #7dd3fc;
      box-shadow: 0 6px 16px rgba(56, 189, 248, 0.16);
      transform: translateY(-1px);
      z-index: 10;

      &::before {
         opacity: 1;
      }
   }

   &:focus-within {
      z-index: 12;
   }
   
   &:last-child {
      margin-bottom: 0;
   }
`;

export const CheckboxInput = styled.input`
   width: 16px;
   height: 16px;
   cursor: pointer;
   accent-color: #0ea5e9;
   margin-top: 2px;
   flex-shrink: 0;
`;

export const CheckboxLabelNew = styled.label`
   font-size: 13px;
   font-weight: 600;
   color: #333;
   cursor: pointer;
   user-select: none;
   flex: 1;
`;

export const CheckboxHelpText = styled.div`
   font-size: 11px;
   color: #64748b;
   font-style: normal;
   line-height: 1.3;
   margin-top: 0;
   margin-left: 24px;
   margin-bottom: 8px;
   padding: 0;
`;

// Clean up and finalize styled components - remove duplicates
export const Styled = styled.div`
   .select { 
      margin-bottom: 0;
      h4 { display: none; }
   }
   .textInput{
      margin: 0;
      h4 {display: none};
   }
`;